<template>
  <v-dialog :value="value" max-width="1000" persistent scrollable @input="$emit('input', $event)">
    <v-card>
      <v-card-title class="primary white--text py-3">
        <v-icon color="white" small class="mr-2">fas fa-file-excel</v-icon>
        {{ batch ? 'Cargar otro Excel a la distribución #' + batch.id + (batch.name ? ' · ' + batch.name : '') : 'Nueva distribución' }}
        <v-spacer />
        <v-btn icon dark small :disabled="working" @click="close"><v-icon small>fas fa-times</v-icon></v-btn>
      </v-card-title>

      <v-card-text class="pt-5" style="max-height: 75vh">
        <!-- Paso 1: OT + datos de las guías + archivo -->
        <template v-if="!validation">
          <div class="caption grey--text font-weight-black text-uppercase mb-2">Orden de trabajo</div>
          <v-row dense v-if="!batch">
            <v-col cols="12" md="3">
              <v-text-field
                v-model="otNumber" label="Orden de Trabajo" placeholder="N° OT" outlined dense type="number"
                :append-icon="otFound ? 'fas fa-check-circle' : 'fas fa-search'" :success="otFound"
                :loading="loadingOt" :readonly="!!workOrderId" :error-messages="otError ? [otError] : []"
                @input="searchOt"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field :value="customer ? customer.name + ' (' + customer.rut + ')' : ''" label="Cliente (de la cotización)" outlined dense readonly />
            </v-col>
            <v-col cols="12" md="3" class="d-flex align-start">
              <v-btn outlined color="primary" block :disabled="!otFound" :loading="downloading" @click="downloadTemplate">
                <v-icon left small>fas fa-download</v-icon>Plantilla
              </v-btn>
            </v-col>
          </v-row>
          <v-alert v-else dense text type="info" class="mb-2">
            Distribución #{{ batch.id }} · OT {{ batch.workorder }} · {{ batch.customer_name }}.
            Las pre-guías se actualizan por N° Local; las ya emitidas o en proceso no se modifican.
          </v-alert>

          <div class="caption grey--text font-weight-black text-uppercase mb-2 mt-2">Datos de las guías</div>
          <v-text-field
            v-model="name" label="Nombre de la distribución (opcional)" outlined dense counter="150" maxlength="150"
            placeholder="Ej: Jumbo Semana Alemana 2026" hint="Si lo deja vacío se usa el nombre del archivo" persistent-hint
            class="mb-2" @input="nameTouched = true"
          />
          <v-row dense>
            <v-col cols="12" md="5">
              <v-select dense outlined hide-details :items="traslados" item-text="text" item-value="value" label="Indicador de traslado" v-model="params.tipo_traslado" />
            </v-col>
            <v-col cols="12" md="5">
              <v-select dense outlined hide-details :items="despachos" item-text="text" item-value="value" label="Tipo de despacho" v-model="params.tipo_despacho" />
            </v-col>
            <v-col cols="6" md="2">
              <v-text-field dense outlined hide-details type="date" label="Fecha guías" v-model="params.fecha" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="N° Orden de compra" v-model="params.oc_folio" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details type="date" label="Fecha OC" v-model="params.oc_fecha" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="Patente" v-model="params.transporte.patente" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="RUT transportista" v-model="params.transporte.rut_trans" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="Chofer" v-model="params.transporte.nombre_chofer" />
            </v-col>
            <v-col cols="6" md="3">
              <v-text-field dense outlined hide-details label="RUT chofer" v-model="params.transporte.rut_chofer" />
            </v-col>
          </v-row>
          <div class="caption grey--text mt-2">
            <v-icon x-small class="mr-1">fas fa-circle-info</v-icon>
            Cada producto de la guía lleva el precio neto unitario de la cotización vigente al momento de emitir.
          </div>

          <div class="caption grey--text font-weight-black text-uppercase mb-2 mt-5">Archivo de distribución</div>
          <v-file-input
            v-model="file" accept=".xlsx" label="Excel (.xlsx)" outlined dense
            prepend-icon="" prepend-inner-icon="fas fa-file-excel"
            hint="Use la plantilla de la OT: N° Local, Nombre Local, Dirección, Comuna y una columna por producto de la cotización con la cantidad"
            persistent-hint
          />
        </template>

        <!-- Paso 2: mapeo de columnas + validación -->
        <template v-else>
          <div class="d-flex flex-wrap align-center mb-3" style="gap: 8px">
            <v-chip label color="primary" dark>{{ validation.locales }} locales</v-chip>
            <v-chip label color="success" dark>{{ validation.created }} pre-guías nuevas</v-chip>
            <v-chip v-if="validation.updated" label color="info" dark>{{ validation.updated }} se actualizan</v-chip>
            <span v-if="validation.customer" class="ml-2">Cliente: <b>{{ validation.customer.name }}</b> ({{ validation.customer.rut }})</span>
          </div>

          <div class="caption grey--text font-weight-black text-uppercase mb-2">Columnas de productos</div>
          <v-alert v-if="validation.needs_mapping" dense text type="warning" class="mb-2">
            Hay columnas que no corresponden a un producto de la cotización. Asigne un producto o márquelas como «No importar».
          </v-alert>
          <v-simple-table dense class="border mb-4">
            <thead>
              <tr><th>Columna del Excel</th><th style="width: 45%">Producto de la cotización</th><th style="width: 120px">Estado</th></tr>
            </thead>
            <tbody>
              <tr v-for="c in validation.columns" :key="c.col">
                <td class="caption">{{ c.header }}</td>
                <td>
                  <span v-if="c.status === 'mapped' && mapping[c.col] === undefined" class="caption">{{ productLabel(c.key) }}</span>
                  <v-select
                    v-else dense hide-details :items="columnOptions" item-text="text" item-value="value"
                    :value="mapping[c.col] !== undefined ? mapping[c.col] : c.key" placeholder="Elegir producto"
                    class="caption" @change="setMapping(c.col, $event)"
                  />
                </td>
                <td>
                  <v-chip x-small :color="columnStatus(c).color" dark>{{ columnStatus(c).text }}</v-chip>
                </td>
              </tr>
            </tbody>
          </v-simple-table>

          <v-alert v-if="otherBatches.length" dense text type="info">
            Esta OT ya tiene la(s) distribución(es) {{ otherBatches.map(id => '#' + id).join(', ') }}. Se creará una distribución más
            (un envío distinto). Si quiere corregir una existente, use «Cargar otro Excel» dentro de ella.
          </v-alert>
          <v-alert v-if="validation.errors.length" dense text type="error">
            <div class="font-weight-bold">{{ validation.errors.length }} filas con error no se cargarán:</div>
            <div v-for="(e, i) in validation.errors.slice(0, 15)" :key="'e' + i" class="caption">{{ e }}</div>
            <div v-if="validation.errors.length > 15" class="caption">… y {{ validation.errors.length - 15 }} más</div>
          </v-alert>
          <v-alert v-if="validation.warnings.length" dense text type="warning">
            <div v-for="(w, i) in validation.warnings.slice(0, 15)" :key="'w' + i" class="caption">{{ w }}</div>
            <div v-if="validation.warnings.length > 15" class="caption">… y {{ validation.warnings.length - 15 }} más</div>
          </v-alert>

          <div class="caption grey--text font-weight-black text-uppercase mb-2">Resumen por producto (con esta carga)</div>
          <product-summary-table :products="validation.product_summary" class="mb-4" />

          <div class="caption grey--text font-weight-black text-uppercase mb-2">Locales</div>
          <v-data-table :headers="previewHeaders" :items="validation.preview" item-key="code" dense :items-per-page="10" class="border" />
        </template>
      </v-card-text>

      <v-divider />
      <v-card-actions class="pa-4">
        <v-btn v-if="validation" text :disabled="working" @click="validation = null"><v-icon left small>fas fa-arrow-left</v-icon>Volver</v-btn>
        <v-spacer />
        <v-btn text :disabled="working" @click="close">Cancelar</v-btn>
        <v-btn v-if="!validation" color="primary" :disabled="!canValidate" :loading="working" @click="validate">
          <v-icon left small>fas fa-check</v-icon>Validar
        </v-btn>
        <template v-else>
          <v-btn v-if="mappingDirty" color="primary" outlined :loading="working" @click="validate">
            <v-icon left small>fas fa-rotate</v-icon>Aplicar asignación
          </v-btn>
          <v-btn color="success" :disabled="!canLoad" :loading="working" @click="load">
            <v-icon left small>fas fa-upload</v-icon>Cargar {{ validation.locales }} pre-guías
          </v-btn>
        </template>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from 'moment'
import notifications from '@/utils/notifications'
import { getWorkOrderforDTE } from '@/api/salesNotes'
import { downloadDispatchTemplate, uploadDispatchDistribution } from '@/api/facturacioncl'
import ProductSummaryTable from '@/components/facturacioncl/DistributionProductSummary.vue'

function emptyParams() {
  return {
    tipo_traslado: 1, tipo_despacho: 1, fecha: moment().format('YYYY-MM-DD'),
    oc_folio: '', oc_fecha: null, transporte: {},
  }
}

const IGNORE = 'ignore'

export default {
  name: 'BulkDispatchImportDialog',
  components: { ProductSummaryTable },
  props: {
    value: { type: Boolean, default: false },
    batch: { type: Object, default: null }, // si viene, se recarga esa distribución
    initialOt: { type: [String, Number], default: null }, // OT sugerida (editable)
    workOrderId: { type: [String, Number], default: null }, // OT fija (desde la cotización/OT)
  },
  data() {
    return {
      otNumber: '',
      otFound: false,
      otError: '',
      loadingOt: false,
      otTimer: null,
      customer: null,
      downloading: false,
      params: emptyParams(),
      file: null,
      name: '',
      nameTouched: false,
      validation: null,
      mapping: {}, // { col: key | 'ignore' } asignado por el usuario
      validatedMapping: '{}',
      working: false,
      traslados: [
        { value: 1, text: '1 - Operación constituye venta' },
        { value: 2, text: '2 - Ventas por efectuar' },
        { value: 3, text: '3 - Consignaciones' },
        { value: 4, text: '4 - Entrega gratuita' },
        { value: 5, text: '5 - Traslados internos' },
        { value: 6, text: '6 - Otros traslados no venta' },
        { value: 7, text: '7 - Guía de devolución' },
      ],
      despachos: [
        { value: 1, text: '1 - Despacho por cuenta del receptor' },
        { value: 2, text: '2 - Por cuenta del emisor a cliente' },
        { value: 3, text: '3 - Por cuenta del emisor a otras instalaciones' },
      ],
      previewHeaders: [
        { text: 'N° Local', value: 'code', width: 90 },
        { text: 'Nombre', value: 'name' },
        { text: 'Dirección', value: 'address' },
        { text: 'Comuna', value: 'comuna' },
        { text: 'Líneas', value: 'lines', align: 'end' },
        { text: 'Unidades', value: 'units', align: 'end' },
      ],
    }
  },
  computed: {
    canValidate() {
      return !!this.file && (!!this.batch || this.otFound) && !this.loadingOt
    },
    canLoad() {
      return !!this.validation && !!this.validation.locales && !this.validation.needs_mapping && !this.mappingDirty
    },
    mappingDirty() {
      return JSON.stringify(this.mapping) !== this.validatedMapping
    },
    otherBatches() {
      const own = this.batch ? this.batch.id : null
      return ((this.validation && this.validation.existing_batches) || []).filter(id => id !== own)
    },
    quotedProducts() {
      return (this.validation && this.validation.product_summary) || []
    },
    columnOptions() {
      return [
        ...this.quotedProducts.map(p => ({ value: p.key, text: '#' + p.key + ' · ' + p.name })),
        { value: IGNORE, text: 'No importar esta columna' },
      ]
    },
  },
  watch: {
    value(open) {
      if (open) this.reset()
    },
    file(f) {
      if (f && !this.nameTouched && !this.batch) this.name = f.name.replace(/\.xlsx?$/i, '')
    },
  },
  methods: {
    reset() {
      this.validation = null
      this.mapping = {}
      this.validatedMapping = '{}'
      this.file = null
      this.name = this.batch ? (this.batch.name || '') : ''
      this.nameTouched = false
      this.otNumber = ''
      this.otFound = false
      this.otError = ''
      this.customer = null
      this.params = emptyParams()
      if (this.batch) {
        this.params = Object.assign(emptyParams(), this.batch.params || {})
        this.params.transporte = Object.assign({}, (this.batch.params && this.batch.params.transporte) || {})
      } else if (this.workOrderId || this.initialOt) {
        this.otNumber = String(this.workOrderId || this.initialOt)
        this.fetchOt()
      }
    },
    close() {
      this.$emit('input', false)
    },
    searchOt() {
      if (this.otTimer) clearTimeout(this.otTimer)
      this.otTimer = setTimeout(this.fetchOt, 700)
    },
    async fetchOt() {
      this.otFound = false
      this.otError = ''
      this.customer = null
      if (!this.otNumber) return
      this.loadingOt = true
      try {
        const data = await getWorkOrderforDTE(this.otNumber)
        this.customer = data.customer ? { id: data.customer.id, name: data.customer.name, rut: data.customer.rut } : null
        if (!this.customer) {
          this.otError = 'La OT no tiene una cotización con cliente'
          return
        }
        this.otFound = true
        if (data.purchase_order && !this.params.oc_folio) this.params.oc_folio = String(data.purchase_order).trim()
      } catch (e) {
        this.otError = 'Orden de Trabajo no encontrada'
      } finally {
        this.loadingOt = false
      }
    },
    async downloadTemplate() {
      this.downloading = true
      try {
        await downloadDispatchTemplate({ workorderId: this.otNumber })
      } catch (e) {
        notifications.showError(typeof e === 'string' ? e : 'No se pudo descargar la plantilla')
      } finally {
        this.downloading = false
      }
    },
    productLabel(key) {
      const p = this.quotedProducts.find(x => x.key === key)
      return p ? '#' + p.key + ' · ' + p.name : '#' + key
    },
    columnStatus(c) {
      const assigned = this.mapping[c.col]
      if (assigned === IGNORE || (assigned === undefined && c.status === 'ignored')) return { text: 'No se importa', color: 'grey' }
      if (assigned !== undefined) return { text: 'Asignada', color: 'success' }
      if (c.status === 'mapped') return { text: 'Confirmada', color: 'success' }
      if (c.status === 'suggested') return { text: 'Sugerida', color: 'info' }
      return { text: 'Sin asignar', color: 'error' }
    },
    setMapping(col, value) {
      this.$set(this.mapping, col, value)
    },
    fields(dryRun) {
      const f = {
        file: this.file, params: this.params, mapping: this.mapping,
        name: (this.name || '').trim(), dry_run: dryRun ? 1 : '',
      }
      if (this.batch) f.batch_id = this.batch.id
      else f.workorder_id = this.otNumber
      return f
    },
    async validate() {
      this.working = true
      try {
        this.validation = await uploadDispatchDistribution(this.fields(true))
        this.validatedMapping = JSON.stringify(this.mapping)
      } catch (e) {
        notifications.showError(typeof e === 'string' ? e : 'No se pudo validar el archivo')
      } finally {
        this.working = false
      }
    },
    async load() {
      this.working = true
      try {
        const batch = await uploadDispatchDistribution(this.fields(false))
        notifications.showSuccess('Distribución cargada', batch.upload_result.created + ' pre-guías nuevas, ' + batch.upload_result.updated + ' actualizadas')
        this.$emit('loaded', batch)
        this.close()
      } catch (e) {
        notifications.showError(typeof e === 'string' ? e : 'No se pudo cargar el archivo')
      } finally {
        this.working = false
      }
    },
  },
}
</script>
